<script setup>
import { ref, computed, defineAsyncComponent, onMounted, onUnmounted, onBeforeMount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import Sidebar from '@/components/Sidebar.vue'
import DashboardHeader from '@/components/DashboardHeader.vue'
import NotificationSystem from '@/components/NotificationSystem.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ImpersonationBanner from '@/components/ImpersonationBanner.vue'
import { useRouteLoading } from '@/composables/useRouteLoading'
import SessionExpiredModal from '@/components/Modals/SessionExpiredModal.vue'
import CompanyLimitReachedModal from '@/components/Modals/CompanyLimitReachedModal.vue'
import PlanExpiredModal from '@/components/Modals/PlanExpiredModal.vue'

// Async: arrastra BrandedCalendar + v-calendar; solo el layout dashboard lo necesita
const AppointmentModal = defineAsyncComponent(() => import('@/components/Modals/AppointmentModal.vue'))
import { usePlanUpgradeModal } from '@/composables/usePlanUpgradeModal'
import { useTheme } from '@/composables/useTheme'
import { useAuthStore } from '@/stores/auth'
import { useAppointmentsStore } from '@/stores/appointments'
import { useClientsStore } from '@/stores/clients'
import { useServicesStore } from '@/stores/services'
import { useUsersStore } from '@/stores/users'
import { useAppStore } from '@/stores/app'
import { validationMessage } from '@/utils/validationErrors'
import { useNotificationsStore } from '@/stores/notifications'
import { companiesService } from '@/services/CompaniesService'
import { setConnectionErrorHandler, setUpgradeRequiredHandler } from '@/services/api'
import { triggerUpgradeRequired } from '@/composables/usePlanUpgradeModal'
import { useSubscriptionCurrent } from '@/composables/useSubscriptionCurrent'
import { useEffectiveAccess } from '@/composables/useEffectiveAccess'
import { subscriptionService } from '@/services/subscriptionService'
import {
  applyBrandingFromLocalStorage,
  applyBrandingFromCompany,
  applyDefaultBranding,
  saveCustomizationToStorage,
  normalizeAppearance,
  resolveCompanyLogoUrl,
  resolveCompanyBannerUrl
} from '@/utils/customization'
import InfoModal from '@/components/Modals/InfoModals/InfoModal.vue'
import MobileBottomNav from '@/components/MobileBottomNav.vue'
import HelpTour from '@/components/Help/HelpTour.vue'
import {
  CheckCircleIcon,
  LockClosedIcon
} from '@heroicons/vue/24/outline'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const appointmentsStore = useAppointmentsStore()
const clientsStore = useClientsStore()
const servicesStore = useServicesStore()
const usersStore = useUsersStore()
const appStore = useAppStore()
const notificationsStore = useNotificationsStore()
const { isDark, toggleTheme } = useTheme()
const { isRouteLoading } = useRouteLoading()

const { accessMessage: subscriptionAccessMessage, downgradeAckPending, refresh: refreshSubscriptionCurrent } = useSubscriptionCurrent()
// El banner/modal de "plan vencido" reflejan el acceso EFECTIVO de la empresa activa
// (estado fresco y compartido), no la facturación cruda del usuario logueado, que puede
// quedar vieja tras un vencimiento/downgrade y dejar el banner colgado en pantalla.
const { payload: effectiveAccessPayload, isAccessActive: effectiveAccessActive } = useEffectiveAccess()
const showExpiredBanner = computed(() => {
  if (!authStore.isAuthenticated) return false
  // Solo el dueño de la empresa activa puede renovar; los workers/miembros no ven el
  // banner aunque el plan del owner haya vencido (las features ya las gatea el efectivo).
  if (!authStore.isBusinessOwner) return false
  if (effectiveAccessPayload.value == null) return false
  return effectiveAccessActive.value === false
})

// Popup de plan vencido: se dispara cuando el backend marca downgrade_ack_pending
// (la sub del usuario fue degradada a free automáticamente y falta avisarle). El flag
// vive en la DB, así que sobrevive reloads hasta que el usuario lo reconoce (ack).
const showExpiredModal = ref(false)
watch(downgradeAckPending, (pending) => {
  if (pending === true) showExpiredModal.value = true
}, { immediate: true })

// Cargamos la suscripción propia del owner de forma global (no solo en la página de
// Suscripción), para que el modal de baja pueda dispararse en cualquier pantalla.
// /current es owner-only, por eso gateamos por isBusinessOwner.
watch(
  () => authStore.isAuthenticated && authStore.isBusinessOwner,
  (isOwner) => { if (isOwner) refreshSubscriptionCurrent() },
  { immediate: true }
)

// D4: App.vue es el único componente siempre montado, así que es el dueño del ciclo de
// vida del polling de notificaciones (el intervalo en sí vive en el store, ver
// stores/notifications.js). Arranca solo con sesión + empresa activa resueltas; se
// detiene en logout (business queda null) sin depender de que el header esté montado.
watch(
  () => authStore.isAuthenticated && !!authStore.business?.id,
  (canPoll) => {
    if (canPoll) {
      notificationsStore.startPolling()
    } else {
      notificationsStore.stopPolling()
    }
  },
  { immediate: true }
)

async function ackDowngradeFlag() {
  try {
    await subscriptionService.ackDowngrade()
    await refreshSubscriptionCurrent()
  } catch (_) {
    // El ack no debe bloquear el cierre del modal; se reintenta en la próxima carga.
  }
}
function continueWithFreePlan() {
  // Reconocemos el aviso y recargamos para que la app re-lea la suscripción ya como free.
  showExpiredModal.value = false
  ackDowngradeFlag().finally(() => window.location.reload())
}
function renewFromExpiredModal() {
  showExpiredModal.value = false
  ackDowngradeFlag()
  router.push('/dashboard/settings/subscription')
}

const {
  visible: showPlanUpgradeModal,
  message: planUpgradeMessage,
  variant: planUpgradeVariant,
  planGateFeature,
  planGateRequiredPlan,
  planGateCurrentPlan,
  close: closePlanUpgradeModal,
  goToSubscription: goToPlanSubscription,
  openFromPlanUpgradeError
} = usePlanUpgradeModal()

const showNewAppointmentModal = ref(false)
const showEmailVerifiedModal = ref(false)
const isBottomNavVisible = ref(true)
const lastScrollY = ref(0)
const lastToggleAt = ref(0)
const scrollThreshold = 5

const setBottomNavVisible = (value) => {
  if (isBottomNavVisible.value === value) return
  isBottomNavVisible.value = value
  lastToggleAt.value = performance.now()
}

// Sin scroll que emitir (vistas cortas), rueda o arrastre hacia arriba son la forma de volver a mostrarla.
const handleWheel = (event) => {
  if (event.deltaY < -4) setBottomNavVisible(true)
}
let touchStartY = null
const handleTouchStart = (event) => { touchStartY = event.touches?.[0]?.clientY ?? null }

// El touch-target de un touchmove es siempre el del touchstart (no se actualiza al dedo moverse):
// si arrancó dentro de un modal/overlay o de un slider con scroll horizontal propio, no es un swipe de la vista.
const isOutsideNavRevealZone = (target) => {
  if (!(target instanceof Element)) return false
  if (target.closest('[role="dialog"], [aria-modal="true"], .fixed.inset-0')) return true
  for (let node = target; node && node !== document.body; node = node.parentElement) {
    if (node.scrollWidth > node.clientWidth) return true
  }
  return false
}

const handleTouchMove = (event) => {
  const y = event.touches?.[0]?.clientY
  if (touchStartY === null || y === undefined || y - touchStartY <= 24) return
  if (isOutsideNavRevealZone(event.target)) return
  setBottomNavVisible(true)
}
const mainContentRef = ref(null)

// El listener en captura recibe scroll de contenedores DISTINTOS (main, grid de
// semana, grid de mes...). La posición previa debe compararse contra el MISMO
// contenedor; si no, la dirección calculada es basura al cambiar de vista.
let lastScrollElement = null

const handleScroll = (event) => {
  const scrollElement = event?.target || document.documentElement
  const currentScrollY = scrollElement.scrollTop || window.scrollY || window.pageYOffset

  if (lastScrollElement !== scrollElement) {
    // Primer evento de este contenedor: solo sincronizar la referencia.
    lastScrollElement = scrollElement
    lastScrollY.value = currentScrollY
    return
  }

  const scrollDifference = currentScrollY - lastScrollY.value

  if (Math.abs(scrollDifference) < scrollThreshold) return
  lastScrollY.value = currentScrollY

  // Cooldown tras cada cambio de visibilidad: ocultar el header colapsa su alto
  // (transición de 300ms) y cambia la altura del contenedor de scroll DURANTE toda
  // la animación, lo que re-clampa scrollTop y re-dispara scroll → rebote/flicker.
  // El cooldown debe superar la duración de la transición (300ms) para cubrir todos
  // esos eventos inducidos. Corta el bucle en CUALQUIER vista (día/semana/mes/lista).
  if (performance.now() - lastToggleAt.value < 400) return

  // Zona del fondo: un fling rápido puede aterrizar acá directo (pocos eventos con
  // saltos grandes), así que bajando SÍ se oculta. Subiendo se ignora: en el borde,
  // los eventos hacia arriba son el clamp inducido por el colapso del header, no el
  // usuario (el scroll up real sale de la zona en ~90px y entra al bloque normal).
  const maxScroll = (scrollElement.scrollHeight || 0) - (scrollElement.clientHeight || 0)
  if (maxScroll > 120 && currentScrollY >= maxScroll - 90) {
    if (scrollDifference > 0) setBottomNavVisible(false)
    return
  }

  // Ocultar apenas se detecta movimiento hacia abajo (sin esperar a pasar 50px);
  // el ruido fino ya lo filtra scrollThreshold y arriba de todo siempre se muestra.
  if (currentScrollY <= 10) {
    setBottomNavVisible(true)
  } else if (scrollDifference > 0) {
    setBottomNavVisible(false)
  } else if (scrollDifference < 0) {
    setBottomNavVisible(true)
  }
}

onBeforeMount(() => {
  // Sin sesión la marca guardada es la del último usuario: login y registro llevan la de GIOR.
  if (authStore.token) applyBrandingFromLocalStorage()
  else applyDefaultBranding()
})

// Las pantallas de invitado (login, registro, invitación) siempre usan la marca de GIOR, aunque el token haya expirado.
watch(
  () => route.meta?.guest,
  (isGuest) => {
    if (isGuest) applyDefaultBranding()
    else if (authStore.business?.appearance) applyBrandingFromCompany({ appearance: authStore.business.appearance })
  },
  { immediate: true }
)

// Cada vista nueva arranca con la navegación visible: la anterior pudo dejarla oculta.
watch(() => route.fullPath, () => setBottomNavVisible(true))

watch(
  () => authStore.business?.appearance,
  (appearance) => {
    if (appearance && typeof appearance === 'object') {
      applyBrandingFromCompany({ appearance })
      saveCustomizationToStorage(appearance) 
    }
  },
  { deep: true, immediate: true }
)

watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (!isAuthenticated) {
      applyDefaultBranding()
    }
  }
)

const syncActiveCompanyBranding = async (force = false) => {
  if (!authStore.isAuthenticated) return

  const storedAppearance = authStore.business?.appearance
  const alreadyHasLogo = !!authStore.business?.logoCompany
  if (!force && storedAppearance?.buttonColor && alreadyHasLogo) return

  let rawCompany = null
  const activeCompany = companiesService.getActiveCompany()

  if (activeCompany?.tenant_id) {
    try {
      const response = await companiesService.getByTenantId(activeCompany.tenant_id)
      const candidate = response.data || response
      if (candidate && !Array.isArray(candidate) && (candidate.id || candidate.name)) {
        rawCompany = candidate
      }
    } catch (_) {}
  }

  if (!rawCompany) {
    try {
      const res = await companiesService.getAll()
      const data = res.data || res
      const list = Array.isArray(data) ? data : (data.companies || data.items || [])

      if (list.length > 0) {
        companiesService.saveCompaniesToLocal(list)
        const activeId = activeCompany?.id
        const match = activeId ? list.find(c => String(c.id) === String(activeId)) : null
        if (!activeId) {
          rawCompany = list[0]
          companiesService.setActiveCompany(rawCompany)
        } else {
          rawCompany = match
        }
      }
    } catch (_) {}
  }

  if (!rawCompany) return

  // Guard: if active company changed during async work, abort to avoid overriding the new selection
  const currentActiveId = companiesService.getActiveCompany()?.id
  if (activeCompany?.id && currentActiveId && String(currentActiveId) !== String(activeCompany.id)) return

  const appearance = normalizeAppearance(rawCompany?.appearance || {})
  const freshLogoUrl = resolveCompanyLogoUrl(rawCompany)
  const freshBannerUrl = resolveCompanyBannerUrl(rawCompany)

  // IMPORTANTE: este sync es SOLO para branding (logo, banner, appearance, datos de
  // contacto/descripción). NO tocamos `name`, `id` ni `tenant_id` aquí — esos son
  // responsabilidad de useCompanySwitcher (síncrono, sin esperar al backend) para evitar
  // que una respuesta lenta sobrescriba el cambio recién hecho con datos viejos.
  const brandingPatch = {
    appearance,
    logoCompany: freshLogoUrl,
    logo: freshLogoUrl,
    bannerCompany: freshBannerUrl,
    email: rawCompany.email ?? authStore.business?.email,
    phone: rawCompany.phone ?? authStore.business?.phone,
    address: rawCompany.address ?? authStore.business?.address,
    // typebusiness es la fuente de verdad; description solo cubre filas viejas que no lo tienen.
    type: rawCompany.business_type_id ?? rawCompany.businessTypeId ?? rawCompany.business_type?.id ?? rawCompany.business_type ?? rawCompany.businessType ?? rawCompany.typebusiness ?? rawCompany.type ?? rawCompany.description ?? authStore.business?.type,
    description: rawCompany.description ?? authStore.business?.description,
  }

  await authStore.updateBusiness(brandingPatch)

  // Enriquece la entrada de localStorage sin sobrescribir name/id/tenant_id que ya
  // estableció el switcher.
  const currentActive = companiesService.getActiveCompany() || {}
  companiesService.setActiveCompany({
    ...currentActive,
    email: brandingPatch.email,
    phone: brandingPatch.phone,
    logo: brandingPatch.logo,
    logoCompany: brandingPatch.logoCompany,
    bannerCompany: brandingPatch.bannerCompany,
    appearance: brandingPatch.appearance
  })
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  // Captura: los eventos de scroll NO burbujean, pero sí se propagan en fase de
  // captura. Así el handler recibe el scroll de CUALQUIER contenedor interno
  // (ej. .time-slots-container en Agenda, listas propias), no solo el de <main>.
  document.addEventListener('scroll', handleScroll, { passive: true, capture: true })
  window.addEventListener('wheel', handleWheel, { passive: true })
  window.addEventListener('touchstart', handleTouchStart, { passive: true })
  window.addEventListener('touchmove', handleTouchMove, { passive: true })
  setTimeout(() => {
    const mainContent = document.querySelector('.dashboard-main')
    if (mainContent) mainContent.addEventListener('scroll', handleScroll, { passive: true })
  }, 100)
})

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('scroll', handleScroll, { capture: true })
    window.removeEventListener('wheel', handleWheel)
    window.removeEventListener('touchstart', handleTouchStart)
    window.removeEventListener('touchmove', handleTouchMove)

    const mainContent = document.querySelector('.dashboard-main')
    if (mainContent) {
      mainContent.removeEventListener('scroll', handleScroll)
    }
  })

  watch(
    () => authStore.user?.id,
    (userId) => {
      if (userId) syncActiveCompanyBranding()
    },
    { immediate: true }
  )

const handleCloseSessionExpiredModal = () => {
  authStore.closeSessionExpiredModal()
}

const emailVerifyError = ref('')
const emailVerifyAlready = ref(false)
const resendingVerification = ref(false)
const resendSuccess = ref(false)

watch(
  () => route.query.email_verified,
  (val) => {
    if (!val) return
    if (val === 'true') {
      showEmailVerifiedModal.value = true
    } else if (val === 'already') {
      emailVerifyAlready.value = true
      showEmailVerifiedModal.value = true
    } else if (val === 'false') {
      emailVerifyError.value = route.query.verify_error || 'No se pudo verificar el correo electrónico.'
      showEmailVerifiedModal.value = true
    }
    router.replace({ ...route, query: { ...route.query, email_verified: undefined, verify_error: undefined } })
  },
  { immediate: true }
)

const closeEmailVerifiedModal = () => {
  showEmailVerifiedModal.value = false
  emailVerifyError.value = ''
  emailVerifyAlready.value = false
  resendSuccess.value = false
}

const handleResendVerification = async () => {
  const email = authStore.user?.email
  if (!email) return
  resendingVerification.value = true
  const result = await authStore.resendVerification(email)
  resendingVerification.value = false
  if (result.success) {
    resendSuccess.value = true
  }
}

// Modales de flujo de contraseña
const showPasswordResetDoneModal = ref(false)

watch(
  () => route.query.password_reset_done,
  (val) => {
    if (val === 'true') {
      showPasswordResetDoneModal.value = true
      router.replace({ ...route, query: { ...route.query, password_reset_done: undefined } })
    }
  },
  { immediate: true }
)

// Modal de error de conexión (global, debounced to avoid multiple triggers)
const showConnectionErrorModal = ref(false)
let connectionErrorDebounce = null
setConnectionErrorHandler(() => {
  if (connectionErrorDebounce) return
  showConnectionErrorModal.value = true
  connectionErrorDebounce = setTimeout(() => { connectionErrorDebounce = null }, 3000)
})

setUpgradeRequiredHandler((message) => {
  triggerUpgradeRequired(message)
})

// Sidebar state
const isMobileMenuOpen = ref(false)
const isSidebarCollapsed = ref(false)
const activePage = ref('dashboard')

const isDashboardRoute = computed(() => {
  if (!authStore.isAuthenticated) return false
  if (route.path.startsWith('/dashboard')) return true
  if (route.path.startsWith('/subscription/')) return true
  return false
})

const isAuthPage = computed(() => {
  return route.path === '/login' || route.path === '/register' || route.path === '/forgot-password' || route.path.startsWith('/reset-password') || route.path === '/verify-email' || route.path.startsWith('/accept-invitation') || route.path === '/welcome' || route.path === '/onboarding'
})

const isErrorPage = computed(() => {
  return route.path === '/error' || route.name === 'NotFound'
})

const isPublicBookingRoute = computed(() => route.name === 'PublicBooking' || route.meta.public === true)

const toggleMobileMenu = () => { 
  isMobileMenuOpen.value = !isMobileMenuOpen.value 
}
const closeMobileMenu = () => { 
  isMobileMenuOpen.value = false 
}
const toggleSidebarCollapse = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
}

watch(
  () => route.path,
  () => { isMobileMenuOpen.value = false }
)

const handleNewAppointment = async (appointmentData, onPersistComplete) => {
  const done = (err, meta) => {
    if (typeof onPersistComplete === 'function') onPersistComplete(err, meta)
  }
  let active = companiesService.getActiveCompany()
  let cid = active?.id
  if (!cid) {
    try {
      const response = await companiesService.getAll()
      const raw = response.data || response || []
      const companies = Array.isArray(raw)
        ? raw.map((c) => ({ ...c, tenant_id: c.tenant_id ?? null }))
        : []
      if (companies.length > 0) {
        const first = companies[0]
        companiesService.setActiveCompany(first)
        companiesService.saveCompaniesToLocal(companies)
        cid = first.id
      }
    } catch (e) {
      console.error('Error obteniendo empresa:', e)
    }
  }
  if (!cid) {
    appStore.addError('No se encontró una empresa asociada')
    done(new Error('no_company'))
    return
  }

  let client
  try {
    client = await clientsStore.resolveClientForAppointment(appointmentData)
  } catch (error) {
    done(error)
    if (openFromPlanUpgradeError(error, 'clients')) return
    appStore.addError('Error al crear la cita: ' + validationMessage(error))
    return
  }
  try {
    const result = await appointmentsStore.createAppointment(cid, {
      ...appointmentData,
      clientId: client.id
    })
    done(null, {
      success: result.apiSuccess === true,
      message: result.apiMessage
    })
    clientsStore.incrementAppointmentCount(client.id)
    if (!result.apiSuccess) {
      showNewAppointmentModal.value = false
      if (result.apiMessage) {
        appStore.addError(result.apiMessage)
      } else {
        appStore.addNotification({
          type: 'success',
          title: 'Cita creada',
          message: 'La cita se ha creado correctamente'
        })
      }
    }
  } catch (error) {
    done(error)
    if (openFromPlanUpgradeError(error, 'appointments')) return
    appStore.addError('Error al crear la cita: ' + validationMessage(error))
  }
}

watch(
  () => appStore.companyChangeKey,
  async (key) => {
    if (key === 0) return
    const companyId = companiesService.getActiveCompany()?.id
    appointmentsStore.fetchAppointments()
    clientsStore.fetchClients()
    servicesStore.fetchServices()
    if (companyId) usersStore.fetchUsers(companyId)
    appStore.fetchAvailability()
    // useCompanySwitcher ya llamó notificationsStore.reset() antes de disparar este
    // evento; acá solo pedimos los datos frescos del tenant recién activado.
    notificationsStore.fetchNotifications()
    await syncActiveCompanyBranding(true)
  }
)
</script>

<template>
  <Teleport to="body">
    <Transition name="app-modal">
      <div
        v-if="isRouteLoading"
        class="fixed inset-0 z-[9000] flex flex-col items-center justify-center gap-3 bg-gray-500/30 backdrop-blur-sm dark:bg-black/50"
        role="status"
        aria-live="polite"
      >
        <LoadingSpinner class="h-10 w-10 text-light-accent dark:text-dark-accent" />
        <p class="text-sm text-light-text-secondary dark:text-dark-text-secondary">Cargando…</p>
      </div>
    </Transition>
  </Teleport>
  <div id="app" class="min-h-screen h-full bg-light-background text-light-text-primary dark:bg-dark-background dark:text-dark-text-primary font-sans antialiased transition-colors duration-300">
    <!-- Banner global de suplantación de solo lectura (handoff desde admin) -->
    <ImpersonationBanner />

    <!-- Layout for Dashboard (authenticated users) -->
    <div v-if="isDashboardRoute" class="flex min-h-full">
      <!-- Sidebar -->
      <Sidebar 
        :activePage="activePage"
        :isMobileMenuOpen="isMobileMenuOpen"
        :isCollapsed="isSidebarCollapsed"
        @update:activePage="activePage = $event"
        @closeMobileMenu="closeMobileMenu"
        @toggleCollapse="toggleSidebarCollapse"
      />
      
      <!-- Main Content Area -->
      <div 
        :class="[
          'flex-1 flex flex-col h-screen overflow-hidden transition-all duration-300',
          // Ajustar margen según el estado del sidebar
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        ]"
      >
        <!-- Dashboard Header -->
        <DashboardHeader
          :isMobileMenuOpen="isMobileMenuOpen"
          :scrollHidden="!isBottomNavVisible"
          @toggleMobileMenu="toggleMobileMenu"
        />
        
        <!-- Banner global: suscripción vencida -->
        <div
          v-if="showExpiredBanner"
          class="flex items-center justify-between gap-3 border-b border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 px-4 py-2 shrink-0"
        >
          <div class="flex items-center gap-2">
            <div class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
              <svg class="h-3 w-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
              </svg>
            </div>
            <p class="text-xs font-medium text-red-800 dark:text-red-300">
              {{ subscriptionAccessMessage || 'Tu suscripción venció. Algunas funciones están bloqueadas.' }}
            </p>
          </div>
          <button
            type="button"
            class="shrink-0 rounded-lg border border-red-200 dark:border-red-700 bg-red-100 dark:bg-red-900/40 px-3 py-1 text-xs font-semibold text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/40 transition"
            @click="router.push('/dashboard/settings/subscription')"
          >
            Renovar
          </button>
        </div>

        <!-- Dashboard Content: flex + min-h-0 para que la vista del calendario ocupe todo el espacio disponible -->
        <main
          ref="mainContentRef"
          :class="[
            'dashboard-main flex-1 flex flex-col min-h-0 p-4 lg:p-6 lg:pb-[5px] overflow-y-auto',
            // Con el drawer abierto se bloquea el gesto táctil del fondo. Se usa
            // touch-none y no overflow-hidden para no arriesgar el scrollTop actual.
            isMobileMenuOpen ? 'touch-none lg:touch-auto' : ''
          ]"
          @scroll="handleScroll"
        >
          <div class="flex-1 min-h-0 flex flex-col">
            <router-view />
          </div>
          <!-- Espaciador para el bottom-nav mobile flotante: decorativo, NUNCA debe capturar taps
               (si el contenido del tab se desborda, este div quedaba encima y bloqueaba los toques).
               Alto = pill (~3.75rem) + --floating-nav-gap, para que nada quede tapado detrás. -->
          <div class="h-[calc(3.75rem+var(--floating-nav-gap))] shrink-0 lg:hidden pointer-events-none" aria-hidden="true"></div>
        </main>

        <!-- Notification System -->
        <NotificationSystem />
      </div>

      <MobileBottomNav
        v-show="!isMobileMenuOpen"
        :visible="isBottomNavVisible"
        @open-new-appointment="showNewAppointmentModal = true"
      />
      <AppointmentModal
        :open="showNewAppointmentModal"
        @close="showNewAppointmentModal = false"
        @save="handleNewAppointment"
      />
    </div>

    <!-- Layout for Auth Pages (Login/Register) - No header, full page -->
    <div v-else-if="isAuthPage" class="min-h-screen h-full">
      <router-view />
      <NotificationSystem />
    </div>

    <!-- Layout for Error Pages - Clean, no distractions -->
    <div v-else-if="isErrorPage" class="min-h-screen h-full">
      <router-view />
    </div>

    <!-- Public booking page: no app chrome, no auth session UX -->
    <div v-else-if="isPublicBookingRoute" class="min-h-screen h-full">
      <router-view />
    </div>

    <!-- Layout for Public Pages (non-authenticated users) -->
    <div v-else class="flex flex-col min-h-screen h-full">
      <!-- Header -->
      <Header />

      <!-- Main Content -->
      <main class="flex-1">
        <router-view />
      </main>

      <!-- Notification System -->
      <NotificationSystem />

      <!-- Footer -->
      <Footer />
    </div>

    <HelpTour />

    <!-- Límites de plan (403 + upgrade_required) y acceso bloqueado (402 PLAN_UPGRADE_REQUIRED) -->
    <CompanyLimitReachedModal
      :visible="showPlanUpgradeModal"
      :message="planUpgradeMessage"
      :variant="planUpgradeVariant"
      :feature="planGateFeature"
      :required-plan="planGateRequiredPlan"
      :current-plan="planGateCurrentPlan"
      @close="closePlanUpgradeModal"
      @go-to-subscription="goToPlanSubscription"
    />

    <!-- Modal de Sesión Expirada (Global) -->
    <SessionExpiredModal
      :isVisible="authStore.showSessionExpiredModal"
      @close="handleCloseSessionExpiredModal"
    />

    <!-- Popup global: plan vencido (una vez por sesión) -->
    <PlanExpiredModal
      :show="showExpiredModal"
      :message="subscriptionAccessMessage"
      @renew="renewFromExpiredModal"
      @continue-free="continueWithFreePlan"
    />

    <!-- Modal de Error de Conexión (Global) -->
    <InfoModal
      :visible="showConnectionErrorModal"
      variant="error"
      title="Error de conexión"
      message="Por el momento no se puede mostrar esta vista, intente más tarde. Gracias."
      primary-label="Entendido"
      @close="showConnectionErrorModal = false"
    />

    <!-- Modal de Email Verificado -->
    <InfoModal
      v-if="!emailVerifyError && !emailVerifyAlready"
      :visible="showEmailVerifiedModal"
      variant="success"
      title="Correo Verificado"
      message="Tu dirección de correo electrónico ha sido confirmada con éxito. Ahora tenés acceso completo a todas las funciones."
      primary-label="Ir al Dashboard"
      @close="closeEmailVerifiedModal"
    />
    <InfoModal
      v-else-if="emailVerifyAlready"
      :visible="showEmailVerifiedModal"
      variant="info"
      :icon="CheckCircleIcon"
      title="Correo ya verificado"
      message="Tu dirección de correo electrónico ya fue verificada anteriormente. No es necesario volver a verificar."
      primary-label="Ir al Dashboard"
      @close="closeEmailVerifiedModal"
    />
    <InfoModal
      v-else
      :visible="showEmailVerifiedModal"
      variant="error"
      title="No se pudo verificar"
      :message="emailVerifyError"
      secondary-message="El enlace puede haber expirado o ya fue utilizado."
      @close="closeEmailVerifiedModal"
    >
      <template #actions>
        <button
          @click="closeEmailVerifiedModal"
          class="w-full rounded-xl bg-light-cta-bg px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-dark-cta-bg dark:text-white"
        >
          Entendido
        </button>
        <p v-if="!resendSuccess" class="mt-4 text-sm text-light-text-secondary dark:text-dark-text-secondary">
          ¿No recibiste el correo?
          <button
            type="button"
            :disabled="resendingVerification"
            @click="handleResendVerification"
            class="text-light-accent dark:text-dark-accent font-semibold hover:underline ml-1 disabled:opacity-50"
          >
            {{ resendingVerification ? 'Enviando...' : 'Reenviar verificación' }}
          </button>
        </p>
        <p v-else class="mt-4 text-sm text-green-600 dark:text-green-400 font-medium">
          Correo reenviado. Revisá tu bandeja de entrada.
        </p>
      </template>
    </InfoModal>

    <!-- Modal de Contraseña Actualizada -->
    <InfoModal
      :visible="showPasswordResetDoneModal"
      variant="success"
      :icon="LockClosedIcon"
      title="Contraseña actualizada"
      message="Tu contraseña fue cambiada exitosamente. Ya podés iniciar sesión con tu nueva contraseña."
      primary-label="Iniciar sesión"
      @close="showPasswordResetDoneModal = false"
    />
  </div>
</template>

<style scoped>
.router-link-active {
  color: rgb(140 90 61); /* light-accent */
  background-color: rgba(140, 90, 61, 0.1);
}

.dark .router-link-active {
  color: rgb(140 90 61); /* dark-accent */
  background-color: rgba(140, 90, 61, 0.2);
}

/* Reducir padding en pantallas muy pequeñas (< 320px) */
@media (max-width: 320px) {
  .dashboard-main {
    padding: 0.25rem;
    padding-bottom: 8rem;
  }
}
</style>